"use client";

import { cn } from "@/lib/utils";
import type { CricketTemplateData } from "../cricket-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type CricketScorecardProps = {
  data: CricketTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function CricketScorecard({
  data,
  participants: _participants,
  variant = "feed",
}: CricketScorecardProps) {
  if (!data) return null;

  const { yourInnings, opponentInnings, result } = data;
  const showScore = data.hasScore !== false;
  if (!showScore) return null;

  const yourWon = result === "won-by-runs" || result === "won-by-wickets";
  const opponentWon = result === "loss";
  const isDraw = result === "draw" || result === "tie";
  const yourWinMethod =
    result === "won-by-wickets"
      ? "Wkts"
      : result === "won-by-runs"
        ? "Runs"
        : null;
  const opponentWinMethod = (() => {
    if (result !== "loss") return null;
    if (data.chasingTeam === "your-team") return "Runs";
    if (data.chasingTeam === "opponent-team") return "Wkts";
    return null;
  })();

  const content = (
    <>
      <div className="mb-3 flex items-center justify-center gap-6">
        <div className="text-center">
          <div
            className={cn(
              "text-3xl font-bold",
              yourWon ? "text-brand" : "text-text-primary",
            )}
          >
            {yourInnings.runs}/{yourInnings.wickets}
          </div>
          {typeof yourInnings.overs === "number" && (
            <div className="text-xs text-text-secondary">
              {yourInnings.overs} ov
            </div>
          )}
          <div className="text-xs text-text-secondary">Team 1</div>
        </div>
        <span className="text-xl font-bold text-text-secondary">vs</span>
        <div className="text-center">
          <div
            className={cn(
              "text-3xl font-bold",
              opponentWon ? "text-error" : "text-text-primary",
            )}
          >
            {opponentInnings.runs}/{opponentInnings.wickets}
          </div>
          {typeof opponentInnings.overs === "number" && (
            <div className="text-xs text-text-secondary">
              {opponentInnings.overs} ov
            </div>
          )}
          <div className="text-xs text-text-secondary">Team 2</div>
        </div>
      </div>

      {/* Result badge */}
      <div className="mb-4 flex justify-center">
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold",
            yourWon && "bg-brand/20 text-brand",
            opponentWon && "bg-error/20 text-error",
            isDraw && "bg-text-secondary/20 text-text-secondary",
          )}
        >
          {yourWon
            ? `🏆 Team 1 Wins${yourWinMethod ? ` (${yourWinMethod})` : ""}`
            : opponentWon
              ? `Team 2 Wins${opponentWinMethod ? ` (${opponentWinMethod})` : ""}`
              : isDraw
                ? "Draw"
                : "In Progress"}
        </span>
      </div>
    </>
  );

  if (variant === "feed") return content;
  return <ScorecardShell variant={variant}>{content}</ScorecardShell>;
}
